import { SelectionChangedEvent } from 'ag-grid-community';
import { Loader } from 'cdk-radial';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useIsFetching, UseQueryResult } from 'react-query';
import { EVENT_NAME, ReconciliationStatus } from 'common/constants';
import { ComposedTable } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { DatePickerChip } from 'components/DatePickerChip';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { SkeletonPageLayout } from 'components/SkeletonLoader/SkeletonPageLayout';
import { getSelectedStoreOption } from 'components/StoreSelect/slice';
import { useFetchReconciliationReport } from 'hooks';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { ReconcileCustomHeader } from './components';
import { ReconciliationStatusFilterChips } from './components/ReconciliationStatusFilterChip';
import { getRCCTotalsInFooter, filterByDate } from './helpers';
import { labelMessages } from './messages';
import { reconciliationReportColumns } from './reconciliationReportColumns';
import {
  setSelectedReconcileRecords,
  getSelectedReconcileRecords
} from './slice';
import { StyledCard, StyledReconcileDatePickerChip } from './styled';
import { TotalBarFooter } from './TotalBarFooter';
import { OptionType, ReconciliationReportDataType } from './types';

export const ReconciliationReportPage = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const isFetchingPDFData = useIsFetching(['pdfGeneration']);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<string[]>([]);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [settledDate, setSettledDate] = useState<string>('');
  const amplitudeFlag = useRef<boolean>(true);
  const selectedStore = useAppSelector(getSelectedStoreOption).value;
  const selectedReconcileRecords = useAppSelector(getSelectedReconcileRecords);
  const [showTableLoader, setTableLoader] = useState<boolean>(false);
  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError
  }: UseQueryResult<ReconciliationReportDataType[], Error> =
    useFetchReconciliationReport(paymentStatuses.join(','));
  const filteredData = settledDate ? filterByDate(data, settledDate) : data;
  const intl = useIntl();
  const amplitude = useAmplitude();
  const excludeCol = [
    intl.formatMessage(labelMessages.statusLabel),
    intl.formatMessage(labelMessages.documentIdLabel),
    intl.formatMessage(labelMessages.payableIdLabel),
    intl.formatMessage(labelMessages.paymentAmountLabel)
  ];

  const options = reconciliationReportColumns(intl, filteredOptions)
    .map(item => ({
      label: item.headerName,
      value: item.field,
      disabled: false
    }))
    .filter(option => !!option.label && !!option.value)
    .filter(item => item.label && !excludeCol.includes(item.label));

  const onSelection = (selectedOptions: OptionType[]) => {
    setFilteredOptions(selectedOptions);
  };

  const handlePaymentStatus = (selectedPaymentStatus: string[]): void => {
    setPaymentStatuses(selectedPaymentStatus);
    setTableLoader(true);
  };

  const onSearchInputChange = (newValue: string) => {
    setSearchInputValue(newValue);
  };

  const handleRowSelection = ({ api }: SelectionChangedEvent): void => {
    dispatch(setSelectedReconcileRecords(api.getSelectedRows()));
  };

  useEffect(() => {
    if (isSuccess || isError) setTableLoader(false);
  }, [dispatch, isSuccess, isError]);

  const {
    totalPending,
    totalSettled,
    totalVoided,
    totalReconciled,
    selectedTotal,
    totalAmount
  } = getRCCTotalsInFooter(data, selectedReconcileRecords);

  useEffect(() => {
    if (amplitude && totalAmount && amplitudeFlag.current) {
      amplitudeTracker(amplitude, EVENT_NAME.OpenReconcileCreditCards, {
        Reconciled: totalReconciled,
        Settled: totalSettled,
        'Total Credit Spend': totalAmount
      });
      amplitudeFlag.current = false;
    }
  }, [amplitude, totalReconciled, totalSettled, totalAmount]);

  if ((isLoading || isFetching) && !showTableLoader) {
    return <SkeletonPageLayout />;
  }

  return (
    <>
      <ScrollToTopButton />
      {!!isFetchingPDFData && <Loader />}
      <StyledCard id="card">
        <ComposedTable<ReconciliationReportDataType>
          onSearchInputChange={onSearchInputChange}
          searchInputValue={searchInputValue}
          showLoader={showTableLoader && isLoading}
          columnDefs={reconciliationReportColumns(intl, filteredOptions)}
          rowData={filteredData}
          tableHeaderTitle={intl.formatMessage(
            labelMessages.reconciliationTableHeaderTitle
          )}
          tableHeaderFilters={{
            selectedStore,
            selectedStatus: paymentStatuses.join(',')
          }}
          totalsInFooter={{
            totalPending,
            totalSettled,
            totalVoided,
            totalReconciled,
            selectedTotal,
            totalAmount
          }}
          downloadDialogTitle={intl.formatMessage(
            labelMessages.reconciliationReportDialogTitle
          )}
          getRowId={param => param.data.documentId}
          scrollToTopButtonShow={false}
          isPrintable={!(isLoading || isFetching)}
          isDownloadable={!(isLoading || isFetching)}
          isSearchable={!(isLoading || isFetching)}
          tableHeaderContent={
            <>
              <StyledReconcileDatePickerChip>
                <DatePickerChip
                  label={labelMessages.datePickerChipLabel}
                  settledDate={settledDate}
                  setSettledDate={setSettledDate}
                />
              </StyledReconcileDatePickerChip>

              <ReconciliationStatusFilterChips
                paymentStatus={paymentStatuses}
                handleReciliationStatusFilter={handlePaymentStatus}
              />

              <ReconcileCustomHeader
                options={options}
                onSelection={onSelection}
              />
            </>
          }
          suppressScrollOnNewData
          isRowSelectable={rowNode =>
            rowNode?.data?.status === ReconciliationStatus.Reconciled ||
            rowNode?.data?.status === ReconciliationStatus.Settled
          }
          onSelectionChanged={handleRowSelection}
        />
      </StyledCard>
      <TotalBarFooter
        data={filteredData}
        selectedReconcileRecords={selectedReconcileRecords}
      />
    </>
  );
};
